import { ObjectManager } from '../../../concepts/objects';
import { Rect } from '../../../platform/geom/Rect';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { OverlayManager } from '../../canvas/overlay/OverlayManager';
import { ActiveObject, resolve } from '../../Container';
import { Audited } from '../auditing/Audited';
import { SelectedVoteRound } from '../voting/SelectedVoteRound';
import { VoteIndex } from '../voting/VoteIndex';
import { resolveActiveVoteRound } from '../voting/VotingHelpers';
import { AutoSpotlightInfo, ManualSpotlightInfo } from './SpotlightProperties';

export class SpotlightOverlay extends ActiveObject {

    @resolve(Camera) public camera!: Camera;
    @resolve(EditorSession) public session!: EditorSession;
    @resolve(ObjectManager) public objects!: ObjectManager;
    @resolve(OverlayManager) public overlays!: OverlayManager;
    @resolve(VoteIndex) public voteIndex!: VoteIndex;

    public start() {

        const handle = this.overlays.createOverlay('spotlight', this.paint.bind(this));
        this.overlays.add(handle);

        this.burden(
            handle,
            this.camera.observe(() => handle.invalidate()),
            this.objects.on('updated', () => handle.invalidate()),
            this.session.observe(AutoSpotlightInfo, () => handle.invalidate()),
            this.session.observe(ManualSpotlightInfo, () => handle.invalidate()),
            this.session.observe(SelectedVoteRound, () => handle.invalidate()),
        );
    }

    private paint(gfx: UniversalSurfaceRenderer) {
        // if (!this.shouldPaint()) return;

        const auto = this.session.get(AutoSpotlightInfo);
        const manual = this.session.get(ManualSpotlightInfo);
        if (!auto.enabled && !manual.enabled) return;

        gfx.setTransform(1, 0, 0, 1, 0, 0);

        gfx.globalAlpha = 0.3;
        gfx.fillStyle = '#000';
        gfx.fillRect(0, 0, gfx.width, gfx.height);

        gfx.globalAlpha = 1;
        gfx.globalCompositeOperation = 'destination-out';

        gfx.setTransform(this.camera.modelViewMatrix);

        if (auto.enabled) {
            const rects = auto.mode === 'objects'
                ? this.getAutoSpotlightBoxesForObjectsMode(auto.target)
                : this.getAutoSpotlightBoxesForVotesMode(auto.target);
            for (const r of rects) {
                gfx.fillCircle(r.center().x, r.center().y, Math.min(r.width, r.height));
            }
        }

        if (manual.enabled) {
            const [x, y, r] = manual.target;
            gfx.fillCircle(x, y, r);
        }
    }

    private getAutoSpotlightBoxesForObjectsMode(userId: string) {
        const rects = [] as Rect[];
        const elmts = this.objects.getAll(Audited);
        for (const e of elmts) {
            if (e.userId !== userId) continue;
            const p = e.as(Physical);
            if (!p) continue;
            rects.push(p.bounds(RelativeTo.Model));
        }
        return rects;
    }

    private getAutoSpotlightBoxesForVotesMode(userId: string) {
        const rects = new Map<string, Rect>();
        const activeRound = resolveActiveVoteRound(this.objects, this.session);
        if (activeRound) {
            const votes = this.voteIndex.lookup(activeRound.id, '*');
            for (const v of votes) {
                if (v.userId !== userId) continue;
                const p = v.parent?.as(Physical);
                if (!p) continue;
                if (rects.has(p.id)) continue;
                rects.set(p.id, p.bounds(RelativeTo.Model));
            }
        }
        return rects.values();
    }
}
